import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faFileAlt, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { markSelectedNotificationAsRead } from '../services/NotificationService';
import AuthenticateService from '../../auth/AuthenticateService';

const NotificationDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { notification } = location.state || {};
  const { token, logout } = AuthenticateService();

  // Move useEffect to top level - before any conditional returns
  useEffect(() => {
    if (notification?.id && token) {
      markSelectedNotificationAsRead(notification.id, token);
    }
  }, [notification, token]);

  // Move the conditional return after all hooks
  if (!notification) {
    return (
      <div className="container mt-5">
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <h4>No notification data found</h4>
            <p className="text-muted">You may have accessed this page directly</p>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-primary mt-3"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatAmount = (amount) => {
    if (!amount || amount === "0.00") return "N/A";
    return `₦${parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status, readAt) => {
    if (readAt) {
      return {
        text: 'Read',
        icon: faCheckCircle,
        color: 'success',
        badgeClass: 'bg-success'
      };
    }

    switch (status) {
      case '1':
        return {
          text: 'Active',
          icon: faCheckCircle,
          color: 'success',
          badgeClass: 'bg-success'
        };
      default:
        return {
          text: 'Inactive',
          icon: faTimesCircle,
          color: 'danger',
          badgeClass: 'bg-danger'
        };
    }
  };

  const statusInfo = getStatusInfo(notification.status, notification.read_at);

  return (
    <div className="container mt-4">
      {/* Header with Back Button */}
      <div className="d-flex align-items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary me-3"
          aria-label="Go back"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2 className="mb-0">Notification Details</h2>
      </div>

      {/* Main Content Card */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white border-bottom-0 pt-4">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-1">{notification.subject}</h3>
              <div className="d-flex align-items-center text-muted">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2 small" />
                <small>{formatDate(notification.created_at)}</small>
              </div>
            </div>
            <span className={`badge ${statusInfo.badgeClass} fs-6 px-3 py-2`}>
              <FontAwesomeIcon icon={statusInfo.icon} className="me-2" />
              {statusInfo.text}
            </span>
          </div>
        </div>

        <div className="card-body">
          {/* Message Section */}
          <div className="mb-4">
            <h5 className="mb-3">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
              Message
            </h5>
            <div className="bg-light p-4 rounded">
              <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {notification.message}
              </p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="row">
            {(notification.amount && parseFloat(notification.amount) > 0) && (
              <div className="col-md-6 mb-3">
                <div className="card h-100 border">
                  <div className="card-body">
                    <h6 className="card-subtitle mb-2 text-muted">Amount</h6>
                    <p className="card-text fs-5 fw-bold text-primary">
                      {formatAmount(notification.amount)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="col-md-6 mb-3">
              <div className="card h-100 border">
                <div className="card-body">
                  <h6 className="card-subtitle mb-2 text-muted">Reference ID</h6>
                  <p className="card-text font-monospace small">
                    {notification.id || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {notification.updated_at && (
              <div className="col-md-6 mb-3">
                <div className="card h-100 border">
                  <div className="card-body">
                    <h6 className="card-subtitle mb-2 text-muted">Last Updated</h6>
                    <p className="card-text small">
                      {new Date(notification.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions Section */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex gap-2">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-outline-primary"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Notifications
              </button>

              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-outline-secondary"
              >
                Go to Dashboard
              </button>

              <button
                onClick={() => window.print()}
                className="btn btn-outline-info ms-auto"
              >
                Print Details
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive CSS for better mobile display */}
      <style jsx>{`
        @media (max-width: 768px) {
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }

          .card-header .badge {
            margin-top: 10px;
            align-self: flex-start;
          }

          .d-flex.gap-2 {
            flex-direction: column;
          }

          .d-flex.gap-2 button {
            width: 100%;
            margin-bottom: 10px;
          }

          .d-flex.gap-2 .ms-auto {
            margin-left: 0 !important;
          }
        }

        .card {
          transition: transform 0.2s;
        }

        .card:hover {
          transform: translateY(-2px);
        }

        .bg-light {
          background-color: #f8f9fa !important;
        }
      `}</style>
    </div>
  );
};

export default NotificationDetails;